/**
 * Affily platform constants.
 *
 * Money rule for the whole codebase: amounts are ALWAYS integer cents and rates
 * are ALWAYS integer basis points (bps). 100 bps = 1%. Nothing monetary is ever
 * stored or computed as a float — see shared/src/money.js for why.
 */

/**
 * What a program pays for, which sets Affily's fee on it. Chosen by the brand
 * and fixed once creators apply, like the currency.
 */
export const PROGRAM_KINDS = /** @type {const} */ (['digital', 'other']);

export const PROGRAM_KIND_LABELS = {
  digital: 'Digital products: courses, e-books, music, software',
  other: 'Anything else: physical goods, services, property, bookings, leads',
};

/**
 * Affily's fee, in basis points of each commission, by what it is paid for —
 * and on competition prizes. 1050 bps = 10.5%. Each fee is split down the
 * middle: the brand pays half on top of the commission, and half comes out of
 * what the creator receives (see `splitFee` in commission.js).
 *
 * The one place these numbers live, overridable per deployment with
 * PLATFORM_FEE_DIGITAL_BPS, PLATFORM_FEE_OTHER_BPS and PLATFORM_FEE_PRIZE_BPS.
 * A partnership freezes the rate it joined at, so a change here only reaches
 * creators who join afterwards.
 */
const env = (name) => (typeof process !== 'undefined' ? process.env?.[name] : undefined);

export const PLATFORM_FEES_BPS = Object.freeze({
  digital: normalizeFeeBps(env('PLATFORM_FEE_DIGITAL_BPS'), 1050, 'PLATFORM_FEE_DIGITAL_BPS'),
  other: normalizeFeeBps(env('PLATFORM_FEE_OTHER_BPS'), 800, 'PLATFORM_FEE_OTHER_BPS'),
  prize: normalizeFeeBps(env('PLATFORM_FEE_PRIZE_BPS'), 1100, 'PLATFORM_FEE_PRIZE_BPS'),
});

/** The fee on a program's commissions. */
export const programFeeBps = (kind) => PLATFORM_FEES_BPS[kind] ?? PLATFORM_FEES_BPS.other;

function normalizeFeeBps(raw, fallback, name) {
  if (raw === undefined || raw === null || raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0 || n > 10_000) {
    throw new Error(`${name} must be an integer between 0 and 10000 (basis points). Received: ${raw}`);
  }
  return n;
}

/**
 * Bank transfer instead of a card, for brands settling large amounts: the
 * money goes to an IBAN Stripe gives the brand, and no card fee is added.
 * EUR only for now (Affily's Stripe account settles in euros).
 */
export const BANK_TRANSFER_MIN_CENTS = 100_000;
export const BANK_TRANSFER_CURRENCIES = /** @type {const} */ (['eur']);

/** How the money is calculated for a partnership. */
export const COMMISSION_TYPES = /** @type {const} */ ([
  'percent_of_sale',
  'flat_per_conversion',
  'flat_per_post',
  // No money at all: the program pays in gifts. Tracked like any other so the
  // brand still sees what the gifts drove, but nothing ever accrues.
  'gift_only',
]);

export const COMMISSION_TYPE_LABELS = {
  percent_of_sale: '% of sale',
  flat_per_conversion: 'Flat per conversion',
  flat_per_post: 'Flat per post',
  gift_only: 'Gift only',
};

/**
 * Commission types that accrue from tracked sales. `flat_per_post` is the
 * exception: it pays per approved deliverable, so it never accrues off a
 * Conversion record.
 */
export const CONVERSION_DRIVEN_TYPES = ['percent_of_sale', 'flat_per_conversion'];

/**
 * Whether a commission pays once or on every renewal for a bounded period.
 *
 * Deliberately bounded. There is no `lifetime` mode: a perpetual claim on
 * someone else's recurring revenue is closer to equity than to a commission,
 * and it cannot be reasoned about at the point a brand agrees to it.
 */
export const RECURRING_MODES = /** @type {const} */ (['one_time', 'fixed_period']);

export const RECURRING_MODE_LABELS = {
  one_time: 'One-time',
  fixed_period: 'Recurring for a fixed period',
};

export const MIN_RECURRING_MONTHS = 1;
export const MAX_RECURRING_MONTHS = 60;

/** Recurring only makes sense where a conversion can repeat — not per post. */
export const RECURRING_CAPABLE_TYPES = ['percent_of_sale', 'flat_per_conversion'];

/**
 * Gifts: free product or experiences a brand offers on top of (or instead of) a
 * commission. How a gift reaches the creator decides what the brand has to
 * collect from them later, so it is a closed list rather than free text.
 */
export const GIFT_FULFILMENT = /** @type {const} */ (['ship', 'booking', 'none']);

export const GIFT_FULFILMENT_LABELS = {
  ship: 'Shipped to you',
  booking: 'Booked / reserved',
  none: 'No fulfilment needed',
};

export const MAX_GIFTS_PER_PROGRAM = 10;
export const MAX_GIFT_NAME_LENGTH = 80;
export const MAX_GIFT_DESCRIPTION_LENGTH = 400;

/** Largest file POST /api/v1/uploads accepts. Longer videos go in as YouTube links. */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/** Photos, clips and YouTube videos on a brand's public page. */
export const MAX_BRAND_GALLERY_ITEMS = 24;
export const MAX_GALLERY_CAPTION_LENGTH = 200;

/**
 * A creator's request for one gift. `requested`, `approved` and `sent` are the
 * "open" states: stock may be held and personal details are kept. The rest are
 * closed, and closing one deletes the creator's address.
 */
export const GIFT_REQUEST_STATUSES = /** @type {const} */ ([
  'requested',
  'approved',
  'declined',
  'cancelled',
  'sent',
  'redeemed',
]);
export const OPEN_GIFT_REQUEST_STATUSES = /** @type {const} */ (['requested', 'approved', 'sent']);

export const GIFT_REQUEST_STATUS_LABELS = {
  requested: 'Requested',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
  sent: 'Sent',
  redeemed: 'Redeemed',
};

/** A creator may ask for at most this many gifts when applying. */
export const MAX_GIFTS_PER_APPLICATION = 3;
/** Personal details are deleted this long after the request at the latest... */
export const GIFT_DETAILS_MAX_RETENTION_DAYS = 120;
/** ...or this long after the brand marks the gift sent, whichever comes first. */
export const GIFT_DETAILS_AFTER_SENT_DAYS = 30;

export const PAYOUT_SCHEDULES = /** @type {const} */ ([
  'net15',
  'net30',
  'net60',
  'monthly',
]);

export const PAYOUT_SCHEDULE_LABELS = {
  net15: 'Net 15',
  net30: 'Net 30',
  net60: 'Net 60',
  monthly: 'Monthly',
};

/**
 * Plain-language meaning, for people who don't live in accounts-payable
 * shorthand. "Net 30" is obvious to a finance team and opaque to a creator
 * deciding whether they can afford to wait.
 */
export const PAYOUT_SCHEDULE_DESCRIPTIONS = {
  net15: 'Paid within 15 days of the commission being approved',
  net30: 'Paid within 30 days of the commission being approved',
  net60: 'Paid within 60 days of the commission being approved',
  monthly: 'Paid in one batch on a fixed day each month',
};

/** Rough days-to-payment, only for sorting and comparison. */
export const PAYOUT_SCHEDULE_DAYS = { net15: 15, net30: 30, net60: 60, monthly: 30 };

export const PLATFORMS = /** @type {const} */ ([
  'instagram',
  'tiktok',
  'youtube',
  'twitch',
  'x',
  'linkedin',
  'pinterest',
  'facebook',
  'twitter_x',
  'blog',
  'newsletter',
  'podcast',
]);

/**
 * `twitter_x` is the same network as `x`. Stored data may still hold it, so the
 * server keeps accepting it, but pickers offer only `x`; a record that already
 * carries the old value keeps showing it (see platformChoices).
 */
export const LEGACY_PLATFORMS = /** @type {const} */ (['twitter_x']);

/** What a picker should offer: the current list, plus any legacy value already in use. */
export const platformChoices = (inUse = []) => [
  ...PLATFORMS.filter((p) => !LEGACY_PLATFORMS.includes(p) || inUse.includes(p)),
];

export const PLATFORM_LABELS = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  twitch: 'Twitch',
  x: 'X',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  facebook: 'Facebook',
  twitter_x: 'X',
  blog: 'Blog',
  newsletter: 'Newsletter',
  podcast: 'Podcast',
};

export const CATEGORIES = /** @type {const} */ ([
  'beauty',
  'skincare',
  'fashion',
  'fitness',
  'health',
  'food',
  'beverage',
  'home',
  'parenting',
  'pets',
  'tech',
  'software',
  'gaming',
  'finance',
  'travel',
  'outdoors',
  'education',
  'sustainability',
  'b2b',
]);

/**
 * Conversion event categories.
 *
 * Categories carry no payout meaning — they exist so a creator reading a
 * program page immediately understands what an action IS, and so funnels can be
 * ordered sensibly (a signup precedes a purchase).
 */
export const EVENT_CATEGORIES = /** @type {const} */ ([
  'purchase',
  'subscription',
  'signup',
  'trial',
  'lead',
  'contact',
  'download',
  'custom',
]);

export const EVENT_CATEGORY_LABELS = {
  purchase: 'Purchase',
  subscription: 'Subscription',
  signup: 'Sign-up',
  trial: 'Free trial',
  lead: 'Lead',
  contact: 'Contact',
  download: 'Download',
  custom: 'Custom',
};

/** Rough funnel position, used only to order events for display. */
export const EVENT_CATEGORY_ORDER = {
  contact: 1,
  download: 2,
  signup: 3,
  lead: 4,
  trial: 5,
  subscription: 6,
  purchase: 7,
  custom: 8,
};

/**
 * How often one referred visitor can trigger the same event.
 *
 * `once_per_visitor` is what a signup or a trial needs — the same person
 * creating two accounts is not two acquisitions. `every` is what a purchase
 * needs, since a repeat customer genuinely is repeat revenue.
 */
export const COUNTING_MODES = /** @type {const} */ (['every', 'once_per_visitor']);

export const COUNTING_MODE_LABELS = {
  every: 'Every time',
  once_per_visitor: 'Once per referred visitor',
};

/** The event every program starts with, standing in for its headline terms. */
export const DEFAULT_EVENT_KEY = 'purchase';

/** Event keys are used in the brand's snippet, so keep them typeable. */
export const EVENT_KEY_PATTERN = /^[a-z][a-z0-9_]{1,38}$/;

export const MAX_EVENTS_PER_PROGRAM = 12;

/**
 * Competitions: prizes a brand pays the creators who lead on one measure when
 * the competition ends. Each prize ranks creators by a single metric:
 *   - `event_count`: how many times the creator's referrals triggered one of the
 *     program's actions (viewings booked, sign-ups…), as Affily tracked them;
 *   - `video_*`: the creator's best single entered video, as TikTok or Instagram
 *     report it through the creator's own connected account.
 */
export const PRIZE_METRICS = /** @type {const} */ ([
  'event_count',
  'video_views',
  'video_shares',
  'video_likes',
  'video_comments',
]);

export const VIDEO_PRIZE_METRICS = ['video_views', 'video_shares', 'video_likes', 'video_comments'];

export const PRIZE_METRIC_LABELS = {
  event_count: 'Most tracked actions',
  video_views: 'Most views on one video',
  video_shares: 'Most shares on one video',
  video_likes: 'Most likes on one video',
  video_comments: 'Most comments on one video',
};

/**
 * How a prize is won: `most` goes to whoever leads at the end; `first_to` to
 * the first video seen at or past a threshold (say, 100,000 views), on any
 * platform, and can be awarded as soon as someone gets there. "Seen" is a read
 * of the video: Affily reads videos close to a threshold every 15 minutes, and
 * two videos past it in the same read go to the one with more.
 */
export const PRIZE_KINDS = /** @type {const} */ (['most', 'first_to']);

/** Videos within this share of a first-to threshold are read more often. */
export const NEAR_THRESHOLD_SHARE = 0.8;

/**
 * How long after a competition ends each platform's numbers are final. YouTube
 * reports shares through Analytics, which settles a couple of days late.
 */
export const FINAL_READ_DELAY_MS = { tiktok: 0, instagram: 0, youtube: 3 * 86_400_000 };

export const MAX_PRIZES_PER_PROGRAM = 10;
export const MIN_PRIZE_CENTS = 100;
export const MAX_COMPETITION_DAYS = 366;

/** Where a competition video can be entered from: the platforms Affily reads metrics from. */
export const SOCIAL_PLATFORMS = /** @type {const} */ (['tiktok', 'instagram', 'youtube']);

/**
 * What a creator can connect in one click: the video platforms above, whose
 * follower counts are read onto the profile too, and Facebook Pages, which
 * Affily reads followers from but no competition videos.
 */
export const CONNECTABLE_PLATFORMS = /** @type {const} */ ([...SOCIAL_PLATFORMS, 'facebook']);

/** Click dedup window: a repeat hit from the same IP+UA inside this many ms is not a new unique click. */
export const CLICK_DEDUP_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Creator codes ("use code RAE10"). Typed by shoppers at checkout, so: letters
 * and digits only, stored uppercase, and short enough to say out loud.
 */
export const PROMO_CODE_MIN_LENGTH = 3;
export const PROMO_CODE_MAX_LENGTH = 20;
export const PROMO_CODE_PATTERN = /^[A-Z0-9]{3,20}$/;

/** What a creator code takes off the shopper's order. */
export const CODE_DISCOUNT_TYPES = /** @type {const} */ (['percent', 'fixed']);

/**
 * How many brands one account may run. A guard against runaway creation (an
 * agent in a loop), not a product limit — raise it if someone needs more.
 */
export const MAX_BRANDS_PER_USER = 10;

/** Guardrails on what a brand may set as a cookie window. */
export const MIN_COOKIE_WINDOW_DAYS = 1;
export const MAX_COOKIE_WINDOW_DAYS = 365;

/** Affily Boost — featured directory placement. Stubbed, not wired to Stripe yet. */
export const BOOST_PRICE_CENTS = 2900;

/** The default for programs made before a brand could choose. */
export const CURRENCY = 'usd';

/**
 * What a program can pay in. Chosen when the program is made and fixed once a
 * creator has applied: every commission, prize and payout on it is in this
 * currency, and amounts in different currencies are never added together.
 */
export const CURRENCIES = ['eur', 'usd', 'gbp'];

export const CURRENCY_LABELS = {
  eur: 'Euro (€)',
  usd: 'US dollar ($)',
  gbp: 'Pound sterling (£)',
};

/**
 * Legal identity, used by the Terms and Privacy pages and the footer.
 *
 * UNCONFIRMED. These are placeholders: the owner must set the real legal entity
 * name (including company type and registered address, if any), a monitored
 * contact address, and the governing law / venue BEFORE LAUNCH. Edit them here
 * and every legal page and the footer follow. `governingLaw` is deliberately
 * null: no jurisdiction has been chosen and none should be guessed.
 */
export const LEGAL = {
  entityName: 'Sensei EOOD',
  contactEmail: 'hello@affily.me',
  /** e.g. 'the laws of England and Wales'. Null renders a visible "not yet set" marker. */
  governingLaw: null,
  /** ISO date both legal pages show as "Last updated". Bump when the text changes. */
  lastUpdated: '2026-11-03',
};

/**
 * Who can see a program's terms. A public program shows them to everyone; a
 * private one only to its brand and to the creators it has invited or
 * accepted. Everyone else with its link sees who runs it and what it is
 * about, and can apply.
 */
export const PROGRAM_VISIBILITIES = /** @type {const} */ (['public', 'private']);

/**
 * A creator's pitch to a brand: a campaign they would make, with the brief and
 * what they ask for. Accepted, it becomes a private program the creator is
 * invited to.
 */
export const PITCH_FORMATS = /** @type {const} */ (['video', 'reel', 'story', 'post', 'live', 'other']);
export const PITCH_FORMAT_LABELS = {
  video: 'Video',
  reel: 'Reel / short',
  story: 'Story',
  post: 'Post',
  live: 'Live stream',
  other: 'Other',
};
/** How a creator can ask to be paid in a pitch: one way per pitch. */
export const PITCH_COMMISSION_TYPES = /** @type {const} */ ([
  'flat_per_post',
  'percent_of_sale',
  'flat_per_conversion',
  'gift_only',
]);
export const PITCH_COMMISSION_TYPE_LABELS = {
  flat_per_post: 'A fixed fee per post or video',
  percent_of_sale: 'A % of each sale',
  flat_per_conversion: 'A fixed amount per sale or lead',
  gift_only: 'The product or a gift only',
};
export const USAGE_RIGHTS = /** @type {const} */ (['none', 'organic', 'paid_ads']);
export const USAGE_RIGHTS_LABELS = {
  none: 'None — the content stays on my channels',
  organic: 'The brand may repost it on its own channels',
  paid_ads: 'The brand may also run it as paid ads',
};
/** A creator can have this many pitches waiting for an answer at once. */
export const MAX_OPEN_PITCHES = 10;

/**
 * Pay per post: how long after a program ends a creator can still send a post
 * for approval, so a video made on the last day can be paid.
 */
export const POST_SUBMISSION_GRACE_DAYS = 14;
